/**
 * filterEngine — compound AND/OR filter trees for every list surface.
 *
 * WHY this exists: the auditor flagged that list filtering was
 * single-field equality (src/lib/crm/viewFilter.ts is flat AND-only).
 * This module is the Attio/Folk-class replacement: a freely-nestable
 * AND/OR tree of typed leaves, with relative-date operators, emptiness
 * checks, and related-record hops ("Companies where ANY Person's title
 * contains VP"). It is the single pure core behind the FilterBar UI
 * (src/components/crm/GridShell.tsx) and saved views
 * (src/lib/crm/savedViewModel.ts).
 *
 * Design contract (mirrors viewFilter.ts / viewConfig.ts):
 * - PURE: no store access, no globals, inputs are never mutated.
 * - Generic: records are plain objects; attrs are looked up by name.
 * - Honest about missing data: comparisons never phantom-match a row
 *   whose attr is absent (`neq` and `isEmpty` are the deliberate
 *   exceptions — a missing field honestly "is empty" and honestly
 *   "does not equal" any concrete value).
 * - An EMPTY group (no children) is the identity — it matches every
 *   row, for both AND and OR, so a half-built filter in the UI never
 *   blanks the list under the operator's cursor.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Every comparison operator a leaf can use. */
export type FilterOp =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'gt'
  | 'lt'
  | 'in'
  | 'isEmpty'
  | 'notEmpty'
  | 'relativeDateWithin'
  | 'relativeDateBefore'
  | 'relativeDateNext';

/**
 * One comparison clause. When `related` is set the leaf is evaluated
 * against the records returned by `ctx.related(via)` instead of the row
 * itself: `anyOf: true` passes when ANY related record matches,
 * `anyOf: false` requires ALL of them to (and at least one to exist —
 * vacuous truth over zero records would phantom-match).
 */
export interface FilterLeaf {
  attr: string;
  op: FilterOp;
  value: unknown;
  related?: { via: string; anyOf: boolean };
}

/** A compound group: AND/OR over child leaves and groups. Nests freely. */
export interface FilterGroup {
  combinator: 'AND' | 'OR';
  children: (FilterLeaf | FilterGroup)[];
}

/** Evaluation context: related-record resolver + injectable clock. */
export interface FilterCtx {
  /** Resolve the related records for a leaf's `related.via` key. */
  related?: (via: string) => Record<string, unknown>[];
  /** Epoch ms "now" for relative-date ops. Defaults to the wall clock. */
  now?: number;
}

const DAY_MS = 86_400_000;

/** Is this node a compound group (vs a leaf clause)? */
export function isGroup(node: FilterLeaf | FilterGroup): node is FilterGroup {
  return 'combinator' in node;
}

// ---------------------------------------------------------------------------
// Leaf evaluation
// ---------------------------------------------------------------------------

/** Parse a field value into epoch ms, or undefined when not date-like. */
function toEpoch(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? undefined : t;
  }
  return undefined;
}

/** "Empty" = null/undefined, empty string (after trim), or empty array. */
function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Evaluate one leaf's operator against one record (no related hop). */
function matchesHere(leaf: FilterLeaf, record: Record<string, unknown>, now: number): boolean {
  const actual = record?.[leaf.attr];
  const expected = leaf.value;

  switch (leaf.op) {
    case 'eq':
      return actual === expected;

    case 'neq':
      // A missing field honestly "does not equal" any concrete value.
      return actual !== expected;

    case 'contains': {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === 'string') {
        return actual.toLowerCase().includes(String(expected).toLowerCase());
      }
      return false; // missing / non-container fields never match
    }

    case 'gt':
    case 'lt': {
      const flip = leaf.op === 'lt';
      if (typeof actual === 'number' && typeof expected === 'number') {
        return flip ? actual < expected : actual > expected;
      }
      if (typeof actual === 'string' && typeof expected === 'string') {
        return flip ? actual < expected : actual > expected;
      }
      return false; // only compare like-with-like; missing never matches
    }

    case 'in':
      return Array.isArray(expected) && expected.includes(actual);

    case 'isEmpty':
      return isEmptyValue(actual);

    case 'notEmpty':
      return !isEmptyValue(actual);

    case 'relativeDateWithin': {
      // "touched in the last N days": now - N days <= t <= now.
      const days = Number(expected);
      if (!Number.isFinite(days)) return false;
      const t = toEpoch(actual);
      if (t === undefined) return false; // missing date never matches
      return t <= now && t >= now - days * DAY_MS;
    }

    case 'relativeDateBefore': {
      // "last touched > N days ago": strictly older than the window.
      // A record NEVER touched has no date and honestly does not match —
      // OR it with `isEmpty` to catch never-touched rows too.
      const days = Number(expected);
      if (!Number.isFinite(days)) return false;
      const t = toEpoch(actual);
      if (t === undefined) return false;
      return t < now - days * DAY_MS;
    }

    case 'relativeDateNext': {
      // "due in the next N days": now <= t <= now + N days (inclusive).
      // Added so viewRuntime can map viewConfig's `relDate {dir:'future'}`
      // onto THIS engine instead of keeping a second operator table alive.
      const days = Number(expected);
      if (!Number.isFinite(days)) return false;
      const t = toEpoch(actual);
      if (t === undefined) return false;
      return t >= now && t <= now + days * DAY_MS;
    }
  }
}

/** Evaluate one leaf, hopping to related records when `related` is set. */
function matchesLeaf(leaf: FilterLeaf, record: Record<string, unknown>, ctx: FilterCtx, now: number): boolean {
  if (!leaf.related) return matchesHere(leaf, record, now);
  const rows = ctx.related?.(leaf.related.via) ?? [];
  if (rows.length === 0) return false; // no related rows can satisfy either mode
  const inner: FilterLeaf = { attr: leaf.attr, op: leaf.op, value: leaf.value };
  return leaf.related.anyOf
    ? rows.some((r) => matchesHere(inner, r, now))
    : rows.every((r) => matchesHere(inner, r, now));
}

// ---------------------------------------------------------------------------
// Tree evaluation
// ---------------------------------------------------------------------------

function evalNode(
  node: FilterLeaf | FilterGroup,
  record: Record<string, unknown>,
  ctx: FilterCtx,
  now: number,
): boolean {
  if (!isGroup(node)) return matchesLeaf(node, record, ctx, now);
  if (node.children.length === 0) return true; // identity — see module docs
  return node.combinator === 'AND'
    ? node.children.every((c) => evalNode(c, record, ctx, now))
    : node.children.some((c) => evalNode(c, record, ctx, now));
}

/** Does one record pass the compound filter tree? Pure. */
export function evalFilter(
  group: FilterGroup,
  record: Record<string, unknown>,
  ctx: FilterCtx = {},
): boolean {
  return evalNode(group, record, ctx, ctx.now ?? Date.now());
}

/**
 * Return the subset of rows that pass the tree, in original order.
 * Single pass: `now` is resolved ONCE so every row sees the same clock,
 * and the tree is walked per-row with no intermediate allocations.
 * Memoize results at the call site keyed by {@link filterKey}. Pure.
 */
export function applyFilter<T extends Record<string, unknown>>(
  rows: T[],
  group: FilterGroup,
  ctx: FilterCtx = {},
): T[] {
  if (group.children.length === 0) return [...rows];
  const now = ctx.now ?? Date.now();
  return rows.filter((r) => evalNode(group, r, ctx, now));
}

// ---------------------------------------------------------------------------
// filterKey — stable, order-independent cache key
// ---------------------------------------------------------------------------

/** JSON with sorted object keys so equal values serialize identically. */
function stableJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'undefined';
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableJson(obj[k])}`).join(',')}}`;
}

function leafKey(leaf: FilterLeaf): string {
  const rel = leaf.related
    ? `~${leaf.related.via}:${leaf.related.anyOf ? 'any' : 'all'}`
    : '';
  return `${leaf.attr} ${leaf.op} ${stableJson(leaf.value)}${rel}`;
}

/**
 * Stable, order-independent key for a filter tree: two trees that hold
 * the same clauses in a different order produce the SAME key, so a
 * memo cache never recomputes for a cosmetic reorder. Pure.
 */
export function filterKey(group: FilterGroup): string {
  const parts = group.children
    .map((c) => (isGroup(c) ? filterKey(c) : leafKey(c)))
    .sort();
  return `${group.combinator}(${parts.join('|')})`;
}

// ---------------------------------------------------------------------------
// Immutable tree editing (the FilterBar's verbs)
// ---------------------------------------------------------------------------

/** A fresh identity group — matches everything until clauses are added. */
export function emptyGroup(): FilterGroup {
  return { combinator: 'AND', children: [] };
}

/** Count leaf clauses in a tree (for badges / empty-state copy). */
export function countLeaves(group: FilterGroup): number {
  return group.children.reduce<number>(
    (n, c) => n + (isGroup(c) ? countLeaves(c) : 1),
    0,
  );
}

/**
 * Immutably rewrite the group addressed by `path` (child indices from
 * the root; `[]` is the root itself). Returns the ORIGINAL tree when
 * the path doesn't address a group — an honest no-op, never a throw.
 */
function updateGroupAt(
  group: FilterGroup,
  path: number[],
  fn: (g: FilterGroup) => FilterGroup,
): FilterGroup {
  if (path.length === 0) return fn(group);
  const [head, ...rest] = path;
  const child = group.children[head];
  if (child === undefined || !isGroup(child)) return group;
  const next = updateGroupAt(child, rest, fn);
  if (next === child) return group;
  const children = group.children.slice();
  children[head] = next;
  return { ...group, children };
}

/**
 * Append a leaf to the group at `path` (indices from the root; `[]` is
 * the root). Immutable — the input tree is never mutated. Omitting
 * `leaf` appends a blank `eq` clause for the FilterBar to fill in.
 */
export function addLeaf(
  group: FilterGroup,
  path: number[],
  leaf: FilterLeaf = { attr: '', op: 'eq', value: '' },
): FilterGroup {
  return updateGroupAt(group, path, (g) => ({
    ...g,
    children: [...g.children, { ...leaf }],
  }));
}

/** Append an empty subgroup to the group at `path`. Immutable. */
export function addGroup(
  group: FilterGroup,
  path: number[],
  combinator: 'AND' | 'OR' = 'AND',
): FilterGroup {
  return updateGroupAt(group, path, (g) => ({
    ...g,
    children: [...g.children, { combinator, children: [] }],
  }));
}

/**
 * Remove the node addressed by `path` (the last index names the child
 * to drop inside its parent group). Removing the root (`[]`) or an
 * out-of-range index is an honest no-op. Immutable.
 */
export function removeNode(group: FilterGroup, path: number[]): FilterGroup {
  if (path.length === 0) return group;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return updateGroupAt(group, parentPath, (g) => {
    if (idx < 0 || idx >= g.children.length) return g;
    return { ...g, children: g.children.filter((_, i) => i !== idx) };
  });
}

/**
 * Replace the leaf addressed by `path` with `leaf` (for FilterBar
 * in-place edits of attr/op/value). Invalid paths are a no-op. Immutable.
 */
export function replaceLeaf(
  group: FilterGroup,
  path: number[],
  leaf: FilterLeaf,
): FilterGroup {
  if (path.length === 0) return group;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return updateGroupAt(group, parentPath, (g) => {
    const existing = g.children[idx];
    if (existing === undefined || isGroup(existing)) return g;
    const children = g.children.slice();
    children[idx] = { ...leaf };
    return { ...g, children };
  });
}

/** Flip a group's combinator (AND↔OR) at `path`. Immutable. */
export function setCombinator(
  group: FilterGroup,
  path: number[],
  combinator: 'AND' | 'OR',
): FilterGroup {
  return updateGroupAt(group, path, (g) =>
    g.combinator === combinator ? g : { ...g, combinator },
  );
}
